import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';

/** How the starting seats get decided — see `src/game/seating.ts`. */
export type SeatingChoice = 'entered' | 'dice';

export interface SetupParams {
  tableId: string;
  playerNames: [string, string, string, string];
  seating: SeatingChoice;
}

export interface SetupActions {
  startGame(params: SetupParams): void;
  openSettings(): void;
  /** Back to mode select — the device's own back button leaves the app entirely (it's an SPA with no real navigation), so this is the only way back in. */
  changeMode(): void;
}

const SEAT_LABELS = ['East (deals first)', 'South', 'West', 'North'];

const PLAYERS_LABEL: Record<SeatingChoice, string> = {
  entered: 'Players (seating order)',
  dice: 'Players (any order)',
};

function placeholderFor(seating: SeatingChoice, i: number): string {
  return seating === 'entered' ? SEAT_LABELS[i]! : `Player ${i + 1}`;
}

export function renderSetup(
  root: HTMLElement,
  defaults: { tableId: string; playerNames: string[]; seating?: SeatingChoice },
  error: string | undefined,
  actions: SetupActions,
): void {
  const seating: SeatingChoice = defaults.seating === 'dice' ? 'dice' : 'entered';

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Mahjong Scorer</h1>
      <p class="muted">Hong Kong Old Style. Enter the table and the four players to start a new game.</p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="tableId">Table ID</label>
      <input type="text" id="tableId" placeholder="e.g. table-3" value="${esc(defaults.tableId)}" />

      <label>Who sits where?</label>
      <div class="seating-choice" role="radiogroup" aria-label="How the seating is decided">
        <label class="seating-option">
          <input type="radio" name="seating" value="entered" ${seating === 'entered' ? 'checked' : ''} />
          <span>
            <strong>I'll enter the seating now</strong>
            <small>Type the players in seating order — East first, then South, West, North.</small>
          </span>
        </label>
        <label class="seating-option">
          <input type="radio" name="seating" value="dice" ${seating === 'dice' ? 'checked' : ''} />
          <span>
            <strong>Decide with the dice</strong>
            <small>Type the four names in any order. Once everyone has rolled the dice, you set who sits at East, South, West and North.</small>
          </span>
        </label>
      </div>

      <div class="row" style="align-items:baseline;justify-content:space-between">
        <label id="playersLabel" style="margin:0">${PLAYERS_LABEL[seating]}</label>
        <button id="clearPlayersBtn" class="ghost" type="button" style="width:auto;padding:0.2rem 0.5rem;font-size:0.8rem">
          Clear Players
        </button>
      </div>
      <div class="stack">
        ${[0, 1, 2, 3]
          .map(
            (i) => `
          <input type="text" id="player-${i}" placeholder="${esc(placeholderFor(seating, i))}" value="${esc(defaults.playerNames[i] ?? '')}" />
        `,
          )
          .join('')}
      </div>

      <button id="startBtn" style="margin-top:1rem;width:100%">Start Game</button>
      <button id="settingsBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">
        House Rules…
      </button>
      <button id="changeModeBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">
        ← Back to Mode Select
      </button>
    </div>
  `;

  const chosenSeating = (): SeatingChoice =>
    root.querySelector<HTMLInputElement>('input[name="seating"]:checked')?.value === 'dice' ? 'dice' : 'entered';

  // Switching only relabels the boxes — anything already typed stays put.
  for (const radio of root.querySelectorAll<HTMLInputElement>('input[name="seating"]')) {
    radio.addEventListener('change', () => {
      const choice = chosenSeating();
      root.querySelector<HTMLLabelElement>('#playersLabel')!.textContent = PLAYERS_LABEL[choice];
      for (let i = 0; i < 4; i++) {
        root.querySelector<HTMLInputElement>(`#player-${i}`)!.placeholder = placeholderFor(choice, i);
      }
    });
  }

  const startBtn = root.querySelector<HTMLButtonElement>('#startBtn')!;
  startBtn.addEventListener('click', () => {
    const tableId = root.querySelector<HTMLInputElement>('#tableId')!.value.trim();
    const playerNames = [0, 1, 2, 3].map(
      (i) => root.querySelector<HTMLInputElement>(`#player-${i}`)!.value.trim(),
    ) as [string, string, string, string];
    // At once: starting can take a few seconds (the check-in), and a button that seems to do nothing gets pressed again.
    startBtn.disabled = true;
    startBtn.textContent = 'Starting…';
    actions.startGame({ tableId, playerNames, seating: chosenSeating() });
  });

  root.querySelector<HTMLButtonElement>('#clearPlayersBtn')!.addEventListener('click', () => {
    for (let i = 0; i < 4; i++) {
      root.querySelector<HTMLInputElement>(`#player-${i}`)!.value = '';
    }
    root.querySelector<HTMLInputElement>('#player-0')!.focus();
  });

  root.querySelector<HTMLButtonElement>('#settingsBtn')!.addEventListener('click', () => {
    actions.openSettings();
  });

  root.querySelector<HTMLButtonElement>('#changeModeBtn')!.addEventListener('click', () => {
    actions.changeMode();
  });
}
